/**
 * Column type inference from observed values.
 */

import { toEpochMs } from '../format';
import type { ColumnType, ValueKind } from './types';

/** Categorical threshold (spec assumption): ≤ 50 distinct values. */
export const CATEGORICAL_MAX_DISTINCT = 50;

/** Plausible epoch-ms window (1990–2100) so counts and IDs are not mistaken for dates. */
const EPOCH_MS_MIN = 631152000000;
const EPOCH_MS_MAX = 4102444800000;

export function valueKind(v: unknown): ValueKind {
  if (Array.isArray(v)) return 'array';
  if (typeof v === 'number') return 'number';
  if (typeof v === 'boolean') return 'boolean';
  if (typeof v === 'string') return 'string';
  return 'object';
}

function looksLikeDateName(name: string): boolean {
  return /date$|_ts$|^ts$|At$/i.test(name);
}

/**
 * Infer a column type from its non-missing values.
 *
 * Strings are a date column only when *every* value parses as ISO-8601 — one free-text
 * value makes it text. Numbers are dates only when the field name says so and the values
 * sit in the epoch-ms range, since most numeric fields here are counts or identifiers.
 */
export function inferColumnType(
  values: unknown[],
  name: string,
  typeCounts: Partial<Record<ValueKind, number>>,
): ColumnType {
  const kinds = Object.keys(typeCounts) as ValueKind[];
  if (kinds.length === 0) return 'text';
  if (kinds.length > 1) return 'mixed';
  switch (kinds[0]) {
    case 'array':
      return 'array';
    case 'object':
      return 'object';
    case 'boolean':
      return 'boolean';
    case 'number': {
      if (
        looksLikeDateName(name) &&
        values.every((v) => typeof v === 'number' && v >= EPOCH_MS_MIN && v <= EPOCH_MS_MAX)
      ) {
        return 'date';
      }
      return 'number';
    }
    case 'string': {
      const nonEmpty = values.filter((v) => v !== '');
      if (nonEmpty.length > 0 && nonEmpty.every((v) => toEpochMs(v) !== null)) return 'date';
      return 'text';
    }
  }
}
